import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreateTenantInput, Tenant, UpdateTenantInput } from "../../lib/realestate";

interface Props {
  mode: "create" | "edit";
  initial?: Tenant;
  onSubmit: (input: CreateTenantInput | UpdateTenantInput) => Promise<void>;
  submitting?: boolean;
  serverError?: string;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n < 0) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}

export function TenantForm({ mode, initial, onSubmit, submitting, serverError }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? "");
  const [unitLabel, setUnitLabel] = useState(initial?.unitLabel ?? "Hauptwohnung");
  const [moveInDate, setMoveInDate] = useState(initial?.moveInDate ?? todayIso());
  const [moveOutDate, setMoveOutDate] = useState(initial?.moveOutDate ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [coldRent, setColdRent] = useState("");
  const [sideCosts, setSideCosts] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (mode === "create") {
      let coldRentCents: number;
      let sideCostsAdvanceCents: number;
      try {
        coldRentCents = parseEuroDe(coldRent);
        sideCostsAdvanceCents = sideCosts.trim() ? parseEuroDe(sideCosts) : 0;
      } catch {
        setLocalError(t("realestate.errors.invalid_amount"));
        return;
      }
      const input: CreateTenantInput = {
        name: name.trim(),
        unitLabel: unitLabel.trim(),
        moveInDate,
        initialRent: { coldRentCents, sideCostsAdvanceCents },
      };
      if (notes.trim()) input.notes = notes.trim();
      await onSubmit(input);
    } else {
      const input: UpdateTenantInput = {};
      if (name !== initial?.name) input.name = name.trim();
      if (unitLabel !== initial?.unitLabel) input.unitLabel = unitLabel.trim();
      if (moveOutDate !== (initial?.moveOutDate ?? "")) {
        input.moveOutDate = moveOutDate.trim() ? moveOutDate : null;
      }
      if (notes !== (initial?.notes ?? "")) input.notes = notes.trim() || null;
      await onSubmit(input);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.tenant.name")}</span>
        <input
          className="block mt-1 w-full border rounded px-2 py-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={120}
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.tenant.unit_label")}</span>
        <input
          className="block mt-1 w-full border rounded px-2 py-1"
          value={unitLabel}
          onChange={(e) => setUnitLabel(e.target.value)}
          required
          maxLength={60}
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.tenant.move_in_date")}</span>
        <input
          type="date"
          className="block mt-1 w-full border rounded px-2 py-1"
          value={moveInDate}
          onChange={(e) => setMoveInDate(e.target.value)}
          required
        />
      </label>
      {mode === "edit" && (
        <label className="block">
          <span className="text-sm font-medium">{t("realestate.tenant.move_out_date")}</span>
          <input
            type="date"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={moveOutDate}
            onChange={(e) => setMoveOutDate(e.target.value)}
          />
          <span className="text-xs text-slate-500">
            {t("realestate.tenant.move_out_date_hint")}
          </span>
        </label>
      )}
      {mode === "create" && (
        <>
          <label className="block">
            <span className="text-sm font-medium">{t("realestate.tenant.cold_rent_initial")}</span>
            <input
              inputMode="decimal"
              className="block mt-1 w-full border rounded px-2 py-1"
              value={coldRent}
              onChange={(e) => setColdRent(e.target.value)}
              placeholder="1.000,00"
              required
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">
              {t("realestate.tenant.side_costs_advance_initial")}
            </span>
            <input
              inputMode="decimal"
              className="block mt-1 w-full border rounded px-2 py-1"
              value={sideCosts}
              onChange={(e) => setSideCosts(e.target.value)}
              placeholder="200,00"
            />
          </label>
        </>
      )}
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.tenant.notes")}</span>
        <textarea
          className="block mt-1 w-full border rounded px-2 py-1"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
          rows={2}
        />
      </label>
      {(localError || serverError) && (
        <div className="text-red-700 text-sm">{localError ?? serverError}</div>
      )}
      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded"
        disabled={submitting}
      >
        {submitting
          ? t("common.submitting")
          : mode === "create"
            ? t("common.create")
            : t("common.save")}
      </button>
    </form>
  );
}
